import React, { useMemo } from 'react';
import { MapPin, X } from 'lucide-react';
import { getRegions } from '../config/locations';

/**
 * Area (town) + zip-code selector used to isolate sensors on the dashboard.
 *
 * Emits a filter object: { town: string|null, zipCode: string|null }.
 * - Picking an Area narrows the Zip dropdown to that area's zips.
 * - Picking a Zip is the finest filter.
 * - "All" on both clears the filter (shows every location).
 */
const RegionSelector = ({ value = {}, onChange, matchCount }) => {
  const { town = null, zipCode = null } = value;
  const regions = useMemo(() => getRegions(), []);

  // Distinct town list (coarse "area") and the zips available for the active town.
  const towns = useMemo(() => {
    const seen = new Map();
    regions.forEach(r => {
      if (!seen.has(r.town)) seen.set(r.town, 0);
      seen.set(r.town, seen.get(r.town) + r.count);
    });
    return Array.from(seen.entries())
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [regions]);

  const zipOptions = useMemo(
    () => regions.filter(r => !town || r.town === town),
    [regions, town]
  );

  const handleTownChange = (e) => {
    const nextTown = e.target.value || null;
    // Reset zip when the area changes (the old zip may not belong to the new area).
    onChange({ town: nextTown, zipCode: null });
  };

  const handleZipChange = (e) => {
    const nextZip = e.target.value || null;
    // Keep town in sync with the chosen zip so the labels stay consistent.
    const region = regions.find(r => r.zipCode === nextZip);
    onChange({ town: region ? region.town : town, zipCode: nextZip });
  };

  const isFiltered = Boolean(town || zipCode);

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
      <span className="flex items-center gap-1 text-sm font-semibold text-gray-700 mr-1">
        <MapPin size={15} className="text-gray-500" />
        Filter
      </span>

      <label className="flex items-center gap-1">
        <span className="text-xs font-medium text-gray-500">Area</span>
        <select
          value={town || ''}
          onChange={handleTownChange}
          className="rounded-md border border-gray-300 px-2 py-1.5 text-sm bg-white"
        >
          <option value="">All areas</option>
          {towns.map(t => (
            <option key={t.name} value={t.name}>
              {t.name} ({t.count})
            </option>
          ))}
        </select>
      </label>

      <label className="flex items-center gap-1">
        <span className="text-xs font-medium text-gray-500">Zip</span>
        <select
          value={zipCode || ''}
          onChange={handleZipChange}
          className="rounded-md border border-gray-300 px-2 py-1.5 text-sm bg-white"
        >
          <option value="">All zips{town ? ` in ${town}` : ''}</option>
          {zipOptions.map(r => (
            <option key={r.zipCode} value={r.zipCode}>
              {r.zipCode} — {r.town} ({r.count})
            </option>
          ))}
        </select>
      </label>

      {typeof matchCount === 'number' && (
        <span className="text-xs text-gray-500">
          {isFiltered
            ? `${matchCount} location${matchCount === 1 ? '' : 's'}`
            : `All ${matchCount}`}
        </span>
      )}

      {isFiltered && (
        <button
          onClick={() => onChange({ town: null, zipCode: null })}
          className="flex items-center gap-1 px-2 py-1 text-sm text-gray-600 rounded hover:bg-gray-100 transition-colors ml-auto"
          title="Clear filter"
        >
          <X size={14} />
          <span>Clear</span>
        </button>
      )}
    </div>
  );
};

export default RegionSelector;
